/**
 * Shop by Color - Lens Shopping Experience
 * Handles color selection, frame search, SKU validation, and add to cart
 */

// Wait for DOM and jQuery
document.addEventListener('DOMContentLoaded', () => {
  // Initialize search data
  if (typeof window.initShopByColor === 'function') {
    window.initShopByColor();
  }

  const topbar = document.getElementById('sbcMobileTopbar');
  if (topbar) {
  const MAX_OFFSET = 100; // px
  const updateTopbar = () => {
    const scrollY = window.scrollY;
    // Reduce the offset proportionally: 0 at 80px or beyond
    const newTop = Math.max(MAX_OFFSET - scrollY, 0);
    topbar.style.top = newTop + 'px';
  };
  updateTopbar();
  window.addEventListener('scroll', updateTopbar, { passive: true });
}

  // Load lens colors data
  let lensColorsData;
  try {
    const lensColorsEl = document.getElementById('lens-colors-data');
    if (lensColorsEl) {
      lensColorsData = JSON.parse(lensColorsEl.textContent);
      window.lensColorsData = lensColorsData; // Make it globally available
      console.log('Loaded lens colors data:', lensColorsData);
    }
  } catch (err) {
    console.error('Failed to parse lens colors data:', err);
  }

  // Wait for jQuery if it's not loaded yet
  const waitForJQuery = (callback) => {
    if (window.jQuery && lensColorsData) {
      callback(lensColorsData);
    } else {
      setTimeout(() => waitForJQuery(callback), 50);
    }
  };

  waitForJQuery((colorData) => {
    'use strict';

  // ============================================
  // URL Parameter Detection & Splash Screen
  // ============================================

  /**
   * Parse URL parameters to support direct links to specific colors/tiers
   * Example URLs:
   * - ?color=G (Standard tier Gray)
   * - ?color=AMBP (AMP tier Blue)
   * - ?color=ZSMNBLP (Pro tier Midnight Blue)
   */
  const urlParams = new URLSearchParams(window.location.search);
  const colorParam = urlParams.get('color');

  /**
   * Decode a color code to extract base color and tier
   * 
   * Color code formats:
   * - "G" = Gray, Standard tier
   * - "AMBP" = AM + B + P = Blue, AMP tier
   * - "ZSMNBLP" = ZS + MNBL + P = Midnight Blue, Pro tier
   * 
   * @param {string} colorCode - The color code to decode
   * @returns {object} { baseColor: string, tier: string }
   */
  const decodeColorCode = (colorCode) => {
    if (!colorCode) return { baseColor: null, tier: 'Standard' };

    const code = colorCode.toUpperCase();

    // Special-case AMP-only V26 codes that may or may not include suffix
    if (code === 'AMV26' || code === 'AMV26P') {
      return { baseColor: 'AMV26', tier: 'AMP' };
    }

    // Check for AMP tier (AM + color + P)
    if (code.startsWith('AM') && code.endsWith('P')) {
      const baseColor = code.slice(2, -1); // Remove AM and P
      if (baseColor === 'V26') {
        return { baseColor: 'AMV26', tier: 'AMP' };
      }
      return { baseColor, tier: 'AMP' };
    }

    // Check for PRO tier (ZS + color + P)
    if (code.startsWith('ZS') && code.endsWith('P')) {
      const baseColor = code.slice(2, -1); // Remove ZS and P
      return { baseColor, tier: 'Pro' };
    }

    // Standard tier (just the color code)
    return { baseColor: code, tier: 'Standard' };
  };

  /**
   * Validate that a color exists in the lens colors data
   * 
   * @param {string} baseColor - The base color code to validate (e.g., "G", "BL")
   * @returns {boolean} True if the color exists in the data
   */
  const validateColorExists = (baseColor) => {
    if (!baseColor) return false;
    const upperBase = baseColor.toUpperCase();
    return colorData.some(color => {
      const key = (color._key || '').toUpperCase();
      if (key === upperBase) return true;
      if (upperBase === 'AMV26' && key === 'AMV26') return true;
      return key === `AM${upperBase}P` || key === `ZS${upperBase}P`;
    });
  };

  /**
   * Find a color by name (case insensitive, forgiving search)
   * Searches both the color name and _key fields
   * Supports partial matching for better user experience
   * 
   * @param {string} searchName - The color name to search for (e.g., "Gray", "Glacier")
   * @returns {object|null} The color object if found, null otherwise
   */
  const findColorByName = (searchName) => {
    if (!searchName) return null;
    const normalizedSearch = searchName.toLowerCase().trim();
    const compactSearch = normalizedSearch.replace(/[\s_-]+/g, '');

    return colorData.find(color => {
      const normalizedName = (color.name || '').toLowerCase().trim();
      const normalizedKey = (color._key || '').toLowerCase().trim();
      const normalizedColorName = (color.colorname || '').toLowerCase().trim();

      const compactName = normalizedName.replace(/[\s_-]+/g, '');
      const compactKey = normalizedKey.replace(/[\s_-]+/g, '');
      const compactColorName = normalizedColorName.replace(/[\s_-]+/g, '');

      return (
        normalizedName === normalizedSearch ||
        normalizedKey === normalizedSearch ||
        normalizedColorName === normalizedSearch ||
        normalizedName.includes(normalizedSearch) ||
        normalizedKey.includes(normalizedSearch) ||
        normalizedColorName.includes(normalizedSearch) ||
        compactName === compactSearch ||
        compactKey === compactSearch ||
        compactColorName === compactSearch ||
        compactName.includes(compactSearch) ||
        compactKey.includes(compactSearch) ||
        compactColorName.includes(compactSearch)
      );
    });
  };

  const resolveSpecData = (tierData, baseData) => {
    if (tierData && typeof tierData === 'object') return tierData;
    if (baseData && typeof baseData === 'object') return baseData;
    return {};
  };

  const lockBodyScroll = () => document.body.classList.add('sbc-lock-scroll');
  const unlockBodyScroll = () => document.body.classList.remove('sbc-lock-scroll');

  const showSplashScreen = () => {
    const splashScreen = document.getElementById('splash-screen');
    if (splashScreen) {
      splashScreen.classList.remove('hidden');
      splashScreen.scrollTop = 0;
    }
    lockBodyScroll();
  };

  const hideSplashScreen = () => {
    const splashScreen = document.getElementById('splash-screen');
    if (splashScreen) {
      splashScreen.classList.add('hidden');
    }
    unlockBodyScroll();
  };

  /**
   * Helper function to generate tier badge HTML
   */
  const getTierBadgeHTML = (tier) => {
    if (tier === 'AMP') {
      return `
        <div class="tier-button tier-button__amp">
          <span><svg viewBox="0 0 215.82 120.38"><g><g><path d="M215.82,51.23a28.13,28.13,0,0,1-28.16,28.13v25.39a.5.5,0,0,1-.5.5H159.39l-4.33-7.9,24.77-13.06a1,1,0,0,0,.15-1.68l-4.2-3.29-14.4-11.27c6.33-5.62,12.6-11.22,18.88-16.83a1,1,0,0,0-.08-1.56L161.93,36.71l-9.75-6.93h0L112,1.26l-.07-.05A.66.66,0,0,1,112.5,0L195.14,24.1A28.12,28.12,0,0,1,215.82,51.23Z"/><path d="M175.22,119.47,159.3,108.11l-2.76-2-14.31-10.2-.73-.5a1,1,0,0,1,.09-1.68l17.83-10.22-5.37-4.29-6-4.78-11.18-8.92a1,1,0,0,1,0-1.54L154,48.86,137.19,29.78h-27.3a1,1,0,0,0-.93.63L96.5,61.8,84,30.41a1,1,0,0,0-.93-.63H43.42a1,1,0,0,0-.87.5L.13,103.75a1,1,0,0,0,.87,1.5H24.41a1,1,0,0,0,.87-.5l7.61-13.19H52.17v12.69a1,1,0,0,0,1,1H73.45a1,1,0,0,0,1-1V58.17l8.81,29.74a1,1,0,0,0,1,.71h24.55a1,1,0,0,0,1-.72l8.76-29.65v47.27l56.31,14.84A.5.5,0,0,0,175.22,119.47ZM43.65,72.93l8.52-14.76V72.93Z"/></g></g></svg></span>Color Enhancing
        </div>
      `;
    } else if (tier === 'Pro') {
      return `
        <div class="tier-button tier-button__pro">
          <div>Fuse <span>PRO</span> Polarized</div>
        </div>
      `;
    } else {
      return `
        <div class="tier-button">
          Standard
        </div>
      `;
    }
  };

  /**
   * Populate the splash screen with color selection cards
   * 
   * This function creates a grid of color cards, showing only ONE instance
   * of each base color (e.g., one "Gray" card instead of separate G/AMGP/ZSGP cards)
   * 
   * Each card displays:
   * - Color image (226-{color}.jpg)
   * - Color name (e.g., "Gray" not "G")
   * - VLT percentage (Visible Light Transmission)
   * - Description
   * - "Best For" use cases
   * 
   * When a color is clicked, it automatically selects the HIGHEST available tier:
   * Priority order: AMP > Pro > Standard
   */
  const populateSplashScreen = () => {
    const splashGrid = document.getElementById('splash-color-grid');
    if (!splashGrid) return;

    showSplashScreen();

    splashGrid.innerHTML = '';

    // Color name to base code mapping
    const colorNameToBaseCode = {
      'Glacier': 'BL',
      'Nova': 'NOV',
      'Cascade': 'ONG',
      'Bronze': 'GM',
      'Carbon': 'CBM',
      'Chrome': 'SSM',
      'Fusion': 'YM',
      'Cosmic': 'PM',
      'Rose Gold': 'RG',
      'Sapphire': 'GR',
      'Brown Gradient': 'GRADB',
      'Gradient Brown': 'GRADB',
      'Grey Gradient': 'GRADG',
      'Gradient Grey': 'GRADG',
      'G15': 'G15',
      'Midnight': 'MNBL',
      'Grey': 'G',
      'Brown': 'B',
      'V26': 'AMV26',
      'Violet': 'AMV26',
      'Photochromatic': 'PH',
      'Photochromic': 'PH',
      'Clear': 'CAR',
      'Blue Light': 'CLBL',
      'Yellow': 'YAR'
    };

    // Create unique colors map by base code
    // Priority: AMP > Pro (ZS) > Standard
    const uniqueColors = {};
    colorData.forEach(color => {
      // Extract base color from _key (remove tier prefixes like AM, ZS)
      let baseColor = color._key;
      if (baseColor.startsWith('AM') && baseColor.endsWith('P')) {
        baseColor = baseColor.slice(2, -1); // Remove AM and P
      } else if (baseColor.startsWith('ZS') && baseColor.endsWith('P')) {
        baseColor = baseColor.slice(2, -1); // Remove ZS and P
      }

      // Use base color as key, prioritize by tier
      if (!uniqueColors[baseColor]) {
        uniqueColors[baseColor] = color;
      } else {
        const existingKey = uniqueColors[baseColor]._key;
        const newKey = color._key;
        
        // Prefer AMP tier (starts with 'AM')
        if (newKey.startsWith('AM') && !existingKey.startsWith('AM')) {
          uniqueColors[baseColor] = color;
        }
        // Then prefer Pro tier (starts with 'ZS') if no AMP
        else if (newKey.startsWith('ZS') && !existingKey.startsWith('AM') && !existingKey.startsWith('ZS')) {
          uniqueColors[baseColor] = color;
        }
      }
    });

    // Function to find color by name - always returns from uniqueColors (prefers AMP tier)
    const findColorByName = (name) => {
      // First try the mapping
      const baseCode = colorNameToBaseCode[name];
      if (baseCode && uniqueColors[baseCode]) {
        return uniqueColors[baseCode];
      }
      
      // If mapping doesn't work, try to find by colorname directly
      // Find all colors with matching name, prefer AMP tier
      const matchingColors = colorData.filter(color => {
        const colorName = color.colorname || '';
        return colorName.toLowerCase() === name.toLowerCase() || 
               colorName.toLowerCase().includes(name.toLowerCase()) ||
               name.toLowerCase().includes(colorName.toLowerCase());
      });
      
      if (matchingColors.length > 0) {
        // Prefer AMP tier, then Pro, then Standard
        let preferredColor = matchingColors.find(c => c._key.startsWith('AM'));
        if (!preferredColor) {
          preferredColor = matchingColors.find(c => c._key.startsWith('ZS'));
        }
        if (!preferredColor) {
          preferredColor = matchingColors[0];
        }
        
        // Extract base code and return from uniqueColors (which already has AMP preference)
        let base = preferredColor._key;
        if (base.startsWith('AM') && base.endsWith('P')) {
          base = base.slice(2, -1);
        } else if (base.startsWith('ZS') && base.endsWith('P')) {
          base = base.slice(2, -1);
        }
        return uniqueColors[base] || preferredColor;
      }
      
      return null;
    };

    // Function to create color cards
    const createColorCard = (color, baseColor) => {
      const colorItem = document.createElement('div');
      colorItem.className = 'splash-color-item';

      // Special handling for Cascade - image uses zsongp instead of ong
      let imageCode = baseColor.toLowerCase();
      if (color.colorname === 'Cascade' || baseColor.toUpperCase() === 'ONG') {
        imageCode = 'zsongp';
      }
      
      const imageUrl = color.primary_image || `https://cdn.shopify.com/s/files/1/1542/2169/files/226-${imageCode}.jpg`;
      const colorName = color.colorname || color.name || baseColor || 'Unknown';
      const vlt = color.vlt ? `${color.vlt}% VLT` : '';
      const description = color['lens-color'] || color.description || color['lens-view'] || 'Premium lens option';
      
      // Get 2 activities from the activities list
      let bestFor = 'Various activities';
      if (color.activities) {
        const activitiesList = color.activities.split(',').map(a => a.trim()).filter(a => a);
        if (activitiesList.length >= 2) {
          bestFor = activitiesList.slice(0, 2).map(a => a.replace(/-/g, ' ')).join(' & ');
        } else if (activitiesList.length === 1) {
          bestFor = activitiesList[0].replace(/-/g, ' ');
        }
      } else if (color['best-for']) {
        // Fallback to best-for if activities not available
        bestFor = color['best-for'].replace(/-/g, ' ');
      }

      colorItem.innerHTML = `
        <img src="${imageUrl}" alt="${colorName}" class="splash-color-image">
        <div class="splash-color-details">
          <div class="splash-color-name">${colorName}</div>
          <div class="splash-color-description">${description}</div>
          <div class="splash-color-vlt">${vlt}</div>
          <div class="splash-color-best-for"><strong>BEST FOR:</strong> ${bestFor}</div>
        </div>
      `;

      colorItem.addEventListener('click', () => {
        // Determine highest available tier for this base color
        // Priority: AMP > Pro (ZS) > Standard
        // Ensure baseColor is uppercase to match tierAvailability array
        const baseColorUpper = baseColor.toUpperCase();
        let selectedTier = 'Standard'; // Default

        if (tierAvailability.AMP.includes(baseColorUpper)) {
          selectedTier = 'AMP';
        } else if (tierAvailability.Pro.includes(baseColorUpper)) {
          selectedTier = 'Pro';
        } else if (tierAvailability.Standard.includes(baseColorUpper)) {
          selectedTier = 'Standard';
        }

        console.log('Splash screen clicked:', baseColor, 'baseColorUpper:', baseColorUpper, 'Selected tier:', selectedTier);

        // Set tier and update UI
        state.tier = selectedTier;
        document.querySelectorAll('input[name="tier"]').forEach(btn => {
          btn.checked = (btn.value === selectedTier);
        });

        // Run logic with base color (will use selectedTier to build full code)
        // Use uppercase baseColor for consistency
        runSwatchClickLogic(baseColorUpper);
        
        // Scroll to top of the page
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      return colorItem;
    };

    // Define groups with specific colors and descriptions
    // Removed duplicates: using only one name per color
    const brightLightColors = ['Glacier', 'Nova', 'Cascade', 'Bronze', 'Carbon', 'Chrome', 'Fusion', 'Cosmic', 'Rose Gold', 'Sapphire'];
    const mixedLightColors = ['Brown Gradient', 'Grey Gradient', 'G15', 'Midnight', 'Grey', 'Brown', 'V26', 'Photochromic'];
    const lowLightColors = ['Clear', 'Blue Light', 'Yellow'];

    // Track displayed base codes to prevent duplicates across all groups
    const displayedBaseCodes = new Set();

    // Helper to get base code from color
    const getBaseCodeFromColor = (color, mappedBaseCode) => {
      if (mappedBaseCode) {
        return mappedBaseCode;
      }
      // Extract from _key if no mapping
      let base = color._key;
      if (base.startsWith('AM') && base.endsWith('P')) {
        base = base.slice(2, -1);
      } else if (base.startsWith('ZS') && base.endsWith('P')) {
        base = base.slice(2, -1);
      }
      return base;
    };

    // Helper to add color card if not already displayed
    const addColorCardIfUnique = (colorName) => {
      const color = findColorByName(colorName);
      if (!color) return false;
      
      const mappedBaseCode = colorNameToBaseCode[colorName];
      const baseCode = getBaseCodeFromColor(color, mappedBaseCode);
      
      // Skip if this base code has already been displayed
      if (displayedBaseCodes.has(baseCode)) {
        console.log('Skipping duplicate color:', colorName, 'baseCode:', baseCode);
        return false;
      }
      
      // Mark as displayed and add the card
      displayedBaseCodes.add(baseCode);
      const colorItem = createColorCard(color, baseCode);
      splashGrid.appendChild(colorItem);
      return true;
    };

    // Add Bright Light group
    const brightHeader = document.createElement('h3');
    brightHeader.textContent = 'Lenses For Bright Light';
    const brightDesc = document.createElement('p');
    brightDesc.textContent = 'Best for days where the sun doesn\'t know when to quit. These lenses cut harsh glare, protect against eye strain, and keep your view crisp under the brightest sun.';
    splashGrid.appendChild(brightHeader);
    splashGrid.appendChild(brightDesc);
    
    brightLightColors.forEach(colorName => {
      addColorCardIfUnique(colorName);
    });

    // Add Mixed Light group
    const mixedHeader = document.createElement('h3');
    mixedHeader.textContent = 'Lenses For Mixed Light';
    const mixedDesc = document.createElement('p');
    mixedDesc.textContent = 'Versatile and ready for anything. These lenses are great for shifting light, maintaining sharp contrast and clear detail from sunny breaks to cloudy skies.';
    splashGrid.appendChild(mixedHeader);
    splashGrid.appendChild(mixedDesc);
    
    mixedLightColors.forEach(colorName => {
      addColorCardIfUnique(colorName);
    });

    // Add Low Light group
    const lowHeader = document.createElement('h3');
    lowHeader.textContent = 'Lenses For Low Light';
    const lowDesc = document.createElement('p');
    lowDesc.textContent = 'Made to keep clarity high when the light runs out. These lenses boost definition so you can see every detail in dawn, dusk, overcast, and indoor conditions.';
    splashGrid.appendChild(lowHeader);
    splashGrid.appendChild(lowDesc);
    
    lowLightColors.forEach(colorName => {
      addColorCardIfUnique(colorName);
    });
  };

  // Function to run the same logic as a swatch click
  const runSwatchClickLogic = (colorKey) => {
    console.log('Running swatch click logic for:', colorKey);

    // Update the color selection
    state.colorCode = colorKey;

    // Update URL parameter with tier-specific code
    updateUrlParameters();

    // Hide splash screen
    const splashScreen = document.getElementById('splash-screen');
    const sidebarMenu = document.getElementById('sidebarMenu');
    if (splashScreen) {
      hideSplashScreen();
      sidebarMenu.classList.add('show-sidebar');
      if (window._updateSidebar) window._updateSidebar();
    }

    // Update visual active state
    if ($swatchList) {
      $swatchList.querySelectorAll('a.active-thumb').forEach(x => x.classList.remove('active-thumb'));
      const swatch = $swatchList.querySelector(`a#${colorKey}`);
      if (swatch) {
        swatch.classList.add('active-thumb');
        console.log('Added active-thumb class to swatch:', swatch);
      } else {
        console.error('Swatch not found for visual update:', colorKey);
      }
    }
    
    // Update mobile toggle with cloned swatch (mobile only)
    updateMobileToggleSwatch();

    // Update tier button states
    console.log('=== SPLASH SCREEN TIER UPDATE ===');
    console.log('Current state.tier:', state.tier);
    const tierButtons = document.querySelectorAll('input[name="tier"]');
    console.log('Found tier buttons:', tierButtons);
    tierButtons.forEach(button => {
      console.log('Checking button:', button.value, 'vs state.tier:', state.tier);
      if (button.value === state.tier) {
        button.checked = true;
        console.log('Set tier button as checked:', button.value);
      } else {
        button.checked = false;
        console.log('Unchecked tier button:', button.value);
      }
    });

    // Run the same logic as the swatch click handler
    const lensData = window.lensColorsData;
    let colorData;
    if (Array.isArray(lensData)) {
      colorData = lensData.find(c => c._key === colorKey);
    }
    
    // Build tier-specific color code for price lookup
    const fullColorCode = buildSkuCode(colorKey, state.tier);
    let tierColorData;
    if (Array.isArray(lensData)) {
      tierColorData = lensData.find(c => c._key === fullColorCode);
    }
    console.log('Tier-specific lookup:', fullColorCode, 'found:', tierColorData?._key, 'price:', tierColorData?.price);

    const specDetails = resolveSpecData(tierColorData, colorData);

    if (colorData) {
      console.log('Found color data:', colorData);

      // Update all the content (same as swatch click handler)
      // Update color name (top and bottom)
      const colorName = document.getElementById('sbc-color-name');
      if (colorName) {
        colorName.textContent = colorData.colorname || colorData['lens-view'] || colorData.name || 'Unknown';
      }

      const colorNameBottom = document.getElementById('sbc-color-name-bottom');
      if (colorNameBottom) {
        colorNameBottom.textContent = colorData.colorname || colorData['lens-view'] || colorData.name || 'Unknown';
      }

      // Update tier subtitle
      const tierSubtitle = document.getElementById('sbc-tier-subtitle');
      if (tierSubtitle) {
        tierSubtitle.innerHTML = getTierBadgeHTML(state.tier);
      }

      const lensDescription = document.getElementById('sbc-lens-description');
      if (lensDescription) {
        lensDescription.textContent = colorData?.['lens-color'] || specDetails?.['lens-color'] || '--';
      }

      // Update .sbc-specs background gradient with primary and secondary colors
      const specsContainer = document.querySelector('.sbc-specs');
      if (specsContainer && colorData.primarycolor && colorData.secondarycolor) {
        specsContainer.style.setProperty('--primarycolor', colorData.primarycolor);
        specsContainer.style.setProperty('--secondarycolor', colorData.secondarycolor);
        console.log('[SBC] Set CSS variables:', {
          primarycolor: colorData.primarycolor,
          secondarycolor: colorData.secondarycolor
        });
      }

      // Update specs (top and bottom)
      const brightness = document.getElementById('sbc-spec-brightness');
      if (brightness) {
        const vlt = (specDetails && specDetails.vlt != null) ? `${specDetails.vlt}%` : '--';
        console.log('[SBC] Updating top VLT for', colorKey, 'tier', state.tier, 'value:', vlt);
        brightness.textContent = vlt;
      }

      const brightnessBottom = document.getElementById('sbc-spec-brightness-bottom');
      if (brightnessBottom) {
        const vlt = (specDetails && specDetails.vlt != null) ? `${specDetails.vlt}%` : '--';
        console.log('[SBC] Updating bottom VLT for', colorKey, 'tier', state.tier, 'value:', vlt);
        brightnessBottom.textContent = vlt;
      }

      const view = document.getElementById('sbc-spec-view');
      if (view) {
        view.textContent = specDetails?.['lens-view'] || '--';
      }

      const viewBottom = document.getElementById('sbc-spec-view-bottom');
      if (viewBottom) {
        viewBottom.textContent = specDetails?.['lens-view'] || '--';
      }

      // Update VLT icon active state based on light condition
      const lightCondition = specDetails?.light; // e.g., "low-light", "mixed-light", "bright-light"
      const vltIcons = document.querySelectorAll('.sbc-vlt-icon');
      vltIcons.forEach(icon => {
        icon.classList.remove('active');
        if (icon.getAttribute('data-light') === lightCondition) {
          icon.classList.add('active');
        }
      });

      const vltLabelValue = document.getElementById('sbc-vlt-label-value');
      if (vltLabelValue) {
        vltLabelValue.textContent = formatLightLabel(lightCondition);
      }

      // Update price based on current tier (use tier-specific data)
      const priceToUse = tierColorData?.price || colorData.price;
      const price = document.getElementById('sbc-price');
      if (price && priceToUse) {
        price.textContent = `$${priceToUse.toFixed(2)}`;
        console.log('Updated price to:', priceToUse, 'from tier data:', tierColorData?._key);
      }

      priceDisplays.forEach(el => {
        if (!el) return;
        el.textContent = priceToUse ? `$${priceToUse.toFixed(2)}` : '$--';
      });

      const priceBottom = document.getElementById('sbc-price-bottom');
      if (priceBottom && priceToUse) {
        priceBottom.textContent = `$${priceToUse.toFixed(2)}`;
      }

      // Update feature cells with checkmarks and X's
      // Use tier-specific data for features that vary by tier
      const featureData = tierColorData || colorData;
      const features = {
        'feat-poly': 'yes', // Always polycarbonate
        'feat-uv': 'yes', // Always UV protected
        'feat-mirrored': featureData['mirrored'],
        'feat-polarized': featureData['polarized'],
        'feat-color-enhancing': featureData['color_enhancing']
      };

      Object.entries(features).forEach(([id, value]) => {
        const element = document.getElementById(id);
        if (element) {
          const iconElement = element.querySelector('.sbc-feature-icon');
          if (iconElement) {
            // Toggle checkx class for X icon
            if (value === 'yes') {
              iconElement.classList.remove('checkx');
              iconElement.setAttribute('data-feature', 'yes');
            } else {
              iconElement.classList.add('checkx');
              iconElement.setAttribute('data-feature', 'no');
            }
          }
          element.style.opacity = '1';
          element.style.filter = 'none';
        }

        // Update bottom features
        const elementBottom = document.getElementById(id + '-bottom');
        if (elementBottom) {
          elementBottom.style.opacity = value ? '1' : '0.3';
          elementBottom.style.filter = value ? 'none' : 'grayscale(100%)';
        }
      });

      // Update activities chips (top section)
      const activitiesListEl = document.getElementById('sbc-activities');
      if (activitiesListEl) {
        const activitiesSource = (tierColorData && tierColorData.activities) ? tierColorData.activities : colorData.activities;
        let activities = [];
        if (activitiesSource) {
          activities = activitiesSource
            .split(',')
            .map(activity => activity.trim())
            .filter(activity => activity.length > 0)
            .slice(0, 4)
            .map(activity => activity.replace(/-/g, ' '));
        }

        console.log('[SBC] Activities update for', colorKey, 'tier', state.tier, activities);

        activitiesListEl.innerHTML = '';
        if (activities.length === 0) {
          const placeholder = document.createElement('li');
          placeholder.textContent = '--';
          activitiesListEl.appendChild(placeholder);
        } else {
          activities.forEach(activity => {
            const chip = document.createElement('li');
            chip.textContent = activity;
            activitiesListEl.appendChild(chip);
          });
        }
      }

      // Update images
      const images = {
        'sbc-primary-image': `https://cdn.shopify.com/s/files/1/1542/2169/files/226-${colorKey.toLowerCase()}.jpg`,
        'sbc-lens-hero': colorData['alt-1'] ? `https://cdn.shopify.com/s/files/1/1542/2169/files/${colorData['alt-1']}` : colorData.primary_image,
        'sbc-alt-1': colorData['alt-2'] ? `https://cdn.shopify.com/s/files/1/1542/2169/files/${colorData['alt-2']}` : colorData.image_full,
        // 'sbc-sim-0': colorData['sim-0'] ? `https://cdn.shopify.com/s/files/1/1542/2169/files/${colorData['sim-0']}` : colorData.image_metal,
        // 'sbc-sim-1': colorData['sim-1'] ? `https://cdn.shopify.com/s/files/1/1542/2169/files/${colorData['sim-1']}` : colorData.image_half,
        'sbc-alt-2': colorData['alt-act'] ? `https://cdn.shopify.com/s/files/1/1542/2169/files/${colorData['alt-act']}` : colorData.primary_image
      };

      Object.entries(images).forEach(([id, url]) => {
        const img = document.getElementById(id);
        if (img && url) {
          console.log('Updating image:', { id, url });
          img.src = url;
        }
      });

      const heroUrl = images['sbc-lens-hero'];
      const alt1Url = images['sbc-alt-1'];
      const alt2Url = images['sbc-alt-2'];

      const alt1Wrapper = document.querySelector('.sbc-alt-1.sbc-image-wrapper');
      if (alt1Wrapper) {
        const shouldShowAlt1 = Boolean(colorData['alt-2']) && alt1Url && alt1Url !== heroUrl;
        alt1Wrapper.style.display = shouldShowAlt1 ? '' : 'none';
      }

      const alt2Wrapper = document.querySelector('.sbc-alt-2.sbc-image-wrapper');
      if (alt2Wrapper) {
        const shouldShowAlt2 = Boolean(colorData['alt-act']) && alt2Url && alt2Url !== heroUrl && alt2Url !== alt1Url;
        alt2Wrapper.style.display = shouldShowAlt2 ? '' : 'none';
      }

      // Update simulator images (sim-0 and sim-1) using tier-specific data
      // Use tier-specific data if available, otherwise fall back to base color data
      const simData = tierColorData || colorData;
      
      // Update sim-0 (no-lens image) - the image inside .cd-resize-img
      const sim0Img = document.querySelector('.cd-resize-img img');
      if (sim0Img && simData['sim-0']) {
        const sim0Url = `https://cdn.shopify.com/s/files/1/1542/2169/files/${simData['sim-0']}`;
        console.log('[SBC] Updating sim-0 (no-lens) image:', sim0Url, 'tier:', state.tier);
        sim0Img.src = sim0Url;
        sim0Img.setAttribute('data-src', sim0Url);
      }
      
      // Update sim-1 (color image) - simImg
      const simImg = document.getElementById('simImg');
      if (simImg && simData['sim-1']) {
        const sim1Url = `https://cdn.shopify.com/s/files/1/1542/2169/files/${simData['sim-1']}`;
        console.log('[SBC] Updating sim-1 (color) image:', sim1Url, 'tier:', state.tier);
        simImg.src = sim1Url;
        simImg.setAttribute('data-src', sim1Url);
      }

      // Update simulator label
      const colorName2 = document.getElementById('colorName2');
      if (colorName2) {
        const displayName = colorData.colorname || colorData.name || 'Unknown';
        colorName2.textContent = displayName;
        console.log('Updating simulator label:', displayName);
      }
    }

    // Update tier availability and validate
    updateTierAvailability(colorKey);
    validateCurrent();
  };

  // Check for URL parameter and handle accordingly
  const handleUrlParameter = () => {
    if (colorParam) {
      console.log('[URL PARAM] Handling color param:', colorParam);
      // First try to decode as a full color code (AMBP, ZSMNBLP, etc.)
      const decoded = decodeColorCode(colorParam);
      console.log('[URL PARAM] Decoded:', decoded);

      if (decoded.baseColor && validateColorExists(decoded.baseColor)) {
        // Valid color code - set the tier first
        console.log('[URL PARAM] Setting state.tier to:', decoded.tier);
        state.tier = decoded.tier;

        // Update the tier radio button
        const tierRadio = document.querySelector(`input[name="tier"][value="${decoded.tier}"]`);
        if (tierRadio) {
          tierRadio.checked = true;
          console.log('[URL PARAM] Tier radio button checked');
        }

        // Set the base color code
        state.colorCode = decoded.baseColor;
        console.log('[URL PARAM] Set state.colorCode to:', decoded.baseColor);

        // Hide splash screen
        const splashScreen = document.getElementById('splash-screen');
        const sidebarMenu = document.getElementById('sidebarMenu');
        if (splashScreen) {
          hideSplashScreen();
          sidebarMenu.classList.add('show-sidebar');
          if (window._updateSidebar) window._updateSidebar();
        }

        // Wait a bit for DOM to be ready, then update the sidebar swatch selection
        setTimeout(() => {
          const swatch = document.querySelector(`#product-swatches-list a#${decoded.baseColor}`);
          console.log('[URL PARAM] Found swatch:', !!swatch, 'id:', decoded.baseColor);
          if (swatch) {
            // Update visual state first
            document.querySelectorAll('#product-swatches-list a.active-thumb').forEach(x => x.classList.remove('active-thumb'));
            swatch.classList.add('active-thumb');
            console.log('[URL PARAM] About to click swatch, current state.tier:', state.tier);
            // Actually click the swatch to trigger all the existing logic
            swatch.click();
          }
        }, 100);
      } else {
        // Try to find by name as fallback
        const matchedColor = findColorByName(colorParam);
        if (matchedColor) {
          state.colorCode = matchedColor._key;

          // Determine the correct tier based on color availability
          console.log('=== TIER DETECTION DEBUG ===');
          console.log('Matched color:', matchedColor);
          console.log('Color key:', matchedColor._key);
          console.log('Tier availability mapping:', tierAvailability);
          console.log('AMP includes NOV?', tierAvailability.AMP.includes(matchedColor._key));
          console.log('Pro includes NOV?', tierAvailability.Pro.includes(matchedColor._key));
          console.log('Standard includes NOV?', tierAvailability.Standard.includes(matchedColor._key));

          let correctTier = 'Standard'; // Default to Standard
          // Check tiers in order of preference: Standard first, then Pro, then AMP
          if (tierAvailability.Standard.includes(matchedColor._key)) {
            correctTier = 'Standard';
            console.log('Setting tier to Standard (preferred)');
          } else if (tierAvailability.Pro.includes(matchedColor._key)) {
            correctTier = 'Pro';
            console.log('Setting tier to Pro');
          } else if (tierAvailability.AMP.includes(matchedColor._key)) {
            correctTier = 'AMP';
            console.log('Setting tier to AMP');
          }

          console.log('Final correct tier:', correctTier);
          state.tier = correctTier;

          // Update tier button visual state
          document.querySelectorAll('input[name="tier"]').forEach(btn => {
            btn.checked = (btn.value === correctTier);
          });

          const splashScreen = document.getElementById('splash-screen');
          const sidebarMenu = document.getElementById('sidebarMenu');
          if (splashScreen) {
            hideSplashScreen();
            sidebarMenu.classList.add('show-sidebar');
            if (window._updateSidebar) window._updateSidebar();
          }

          setTimeout(() => {
            const swatch = document.querySelector(`#product-swatches-list a#${matchedColor._key}`);
            if (swatch) {
              // Update visual state first
              document.querySelectorAll('#product-swatches-list a.active-thumb').forEach(x => x.classList.remove('active-thumb'));
              swatch.classList.add('active-thumb');
              swatch.click();
            }
          }, 100);
        } else {
          // Invalid color parameter - show splash screen and clean URL
          console.log('Invalid color parameter, showing splash screen:', colorParam);

          // Clean the URL by removing the invalid parameter
          const url = new URL(window.location);
          url.searchParams.delete('color');
          window.history.replaceState({}, '', url);

          populateSplashScreen();
        }
      }
    } else {
      // No color parameter - show splash screen
      populateSplashScreen();
    }
  };

  // Run URL parameter handling after a short delay to ensure DOM is ready
  setTimeout(handleUrlParameter, 200);

  // ============================================
  // URL Parameter Management
  // ============================================

  // Function to update URL parameters based on current state
  const updateUrlParameters = () => {
    const url = new URL(window.location);

    // Determine the full color code based on tier using buildSkuCode
    // This ensures proper handling of exceptions like V26
    const fullColorCode = buildSkuCode(state.colorCode, state.tier);

    url.searchParams.set('color', fullColorCode);
    window.history.pushState({}, '', url);
  };

  // ============================================
  // Configuration & State
  // ============================================
  
  const cfg = window.shopByColorConfig || {};
  
  const state = {
    colorCode: 'BL',      // default Glacier
    tier: 'AMP',         // default
    handle: '',          // from input
    matchedVariant: null // { id, available, price }
  };

  // ============================================
  // Tier Availability Mapping
  // ============================================

  /**
   * Tier Availability Mapping
   * 
   * This object defines which lens colors are available in each tier.
   * Used to enable/disable tier buttons based on the selected color.
   * 
   * Tier Hierarchy:
   * - AMP: Premium color-enhancing lenses (most limited selection)
   * - Pro: Enhanced quality lenses (moderate selection)
   * - Standard: Essential protection lenses (full selection)
   * 
   * Example: "NOV" (Nova) is available in all tiers
   * Example: "CAR" (Caribbean) is only available in Standard tier
   * 
   * When a color is selected, tiers that don't include that color
   * will be visually disabled (dimmed, non-clickable).
   */
  const tierAvailability = {
    // AMP tier colors - Premium color-enhancing lenses
    'AMP': ['AMV26', 'B', 'BL', 'CBM', 'G', 'G15', 'GM', 'GR', 'NOV', 'ONG'],

    // Pro tier colors - Enhanced quality lenses
    'Pro': ['B', 'BL', 'CBM', 'G', 'G15', 'GM', 'GR', 'GRADB', 'GRADG', 'MNBL', 'NOV', 'ONG', 'PM', 'RG', 'SSM', 'YM'],

    // Standard tier colors - All colors available in Standard
    'Standard': ['B', 'BL', 'CAR', 'CBM', 'CLBL', 'G', 'G15', 'GM', 'GR', 'GRADB', 'GRADG', 'MNBL', 'NOV', 'ONG', 'PH', 'PM', 'RG', 'SSM', 'YAR', 'YM']
  };

  // ============================================
  // DOM Elements
  // ============================================
  
  const $inputDesktop = document.getElementById('selectedFrameField');
  const $inputMobile = document.getElementById('selectedFrameFieldMobile');
  const $inputModal = document.getElementById('fspInput');
  const $priceDesktop = document.getElementById('priceDisplay');
  const $priceMobile = document.getElementById('priceDisplayMobile');
  const $atcDesktop = document.getElementById('sbcAtcButton');
  const $atcMobile = document.getElementById('sbcAtcButtonMobile');
  const $availabilityDesktop = document.getElementById('availabilityMessage');
  const $availabilityMobile = document.getElementById('availabilityMessageMobile');
  const $selectedFrameLabelDesktop = document.querySelector('#selectedFrame .sbc-frame-label');
  const $selectedFrameLabelMobile = document.querySelector('#selectedFrameMobile .sbc-frame-label');
  const $swatchList = document.getElementById('product-swatches-list');
  const $tierRadios = document.querySelectorAll('input[name="tier"]');
  const $searchWrapDesktop = document.querySelector('.sidebar .sbc-search-section');
  const $searchWrapMobile = document.querySelector('.sbc-mobile-bottombar .sbc-search-section');
  const $searchbarDesktop = document.getElementById('SBA__searchbar');
  const $searchbarMobile = document.getElementById('SBA__searchbar-mobile');
  const $resultsListDesktop = document.getElementById('frameresultsUl');
  const $resultsListMobile = document.getElementById('frameresultsUlMobile');

  const frameInputs = [$inputDesktop, $inputMobile, $inputModal].filter(Boolean);
  const priceDisplays = [$priceDesktop, $priceMobile].filter(Boolean);
  const atcButtons = [$atcDesktop, $atcMobile].filter(Boolean);
  const availabilityMessages = [$availabilityDesktop, $availabilityMobile].filter(Boolean);
  const selectedFrameLabels = [$selectedFrameLabelDesktop, $selectedFrameLabelMobile].filter(Boolean);
  const searchWraps = [$searchWrapDesktop, $searchWrapMobile].filter(Boolean);
  const searchbars = [$searchbarDesktop, $searchbarMobile].filter(Boolean);
  const resultsLists = [$resultsListDesktop, $resultsListMobile].filter(Boolean);

  // ============================================
  // Utility Functions
  // ============================================
  
  const toSlug = (s) =>
    s.toLowerCase()
    .trim()
    .replace(/\s+/g, '-')        // spaces → dash
    .replace(/[()]/g, '')        // remove parentheses
    .replace(/,/g, '')           // remove commas
    .replace(/&/g, '')           // remove ampersands
    .replace(/\./g, '-');        // periods → dash

  const normalizeBrandTypos = (str = '') => {
    let s = (str || '').replace(/\s+/g, ' ').trim();
    s = s.replace(/\bray\s*ban\b/gi, 'Ray-Ban');
    s = s.replace(/\brayban\b/gi, 'Ray-Ban');
    s = s.replace(/\boakely\b/gi, 'Oakley');
    return s;
  };

  const buildSkuCode = (c, tier) => {
    const code = (c || '').toUpperCase();

    // AMP-only exception without trailing P
    if (code === 'AMV26' || code === 'V26') {
      return 'AMV26';
    }

    if (tier === 'AMP') {
      return `AM${code}P`;
    }
    if (tier === 'Pro')  return `ZS${code}P`;
    // Standard tier uses the base color code as-is
    return code; // Standard
  };

  const formatLightLabel = (light) => {
    if (!light) return '--';
    return light
      .split(/[\s-]+/)
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const money = (cents) => {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.Shopify.money_format || '${{amount}}');
    }
    return `$${(cents / 100).toFixed(2)}`;
  };

  const setLoading = (on) => {
    searchWraps.forEach(el => el && el.classList.toggle('is-loading', !!on));
  };

  const setValid = (ok) => {
    searchWraps.forEach(el => {
      if (!el) return;
      el.classList.toggle('is-valid-selection', !!ok);
      el.classList.toggle('is-invalid-selection', !ok);
    });
  };

  const showMsg = (msg) => {
    availabilityMessages.forEach(el => el && (el.textContent = msg || ''));
  };

  const resetUI = () => {
    priceDisplays.forEach(el => el && (el.textContent = '$--'));
    atcButtons.forEach(btn => {
      if (!btn) return;
      btn.disabled = true;
      btn.textContent = 'Select Frame to Continue';
    });
    showMsg('');
    setValid(false);
    clearResults();
    if (!state.handle) {
      frameInputs.forEach(input => input && (input.value = ''));
      setSearchbarActive(false);
    }
    state.matchedVariant = null;
  };

  const clearResults = () => {
    resultsLists.forEach(list => list && (list.innerHTML = ''));
  };

  const setSearchbarActive = (active) => {
    searchbars.forEach(bar => bar && bar.classList.toggle('SBA__searchbar--is-searching', !!active));
    frameInputs.forEach(input => input && input.classList.toggle('SBA__searchbar--is-searching', !!active));
  };

    // ============================================
  // Tier Isolation Functions
  // ============================================

  const updateTierAvailability = (colorCode) => {
    console.log('Updating tier availability for color:', colorCode);

    // Enable/disable tier buttons based on color availability
    $tierRadios.forEach(radio => {
      const tier = radio.value;
      const isAvailable = tierAvailability[tier] && tierAvailability[tier].includes(colorCode);

      // Disable/enable the radio button
      radio.disabled = !isAvailable;

      // Add/remove disabled class for styling
      const tierButton = radio.closest('label') || radio.parentElement;
      if (tierButton) {
        tierButton.classList.toggle('tier-disabled', !isAvailable);
      }

      console.log(`Tier ${tier} for color ${colorCode}:`, isAvailable ? 'available' : 'disabled');
    });

    // If current tier is not available, switch to first available tier
    const currentTierAvailable = tierAvailability[state.tier] && tierAvailability[state.tier].includes(colorCode);
    if (!currentTierAvailable) {
      const availableTier = Object.keys(tierAvailability).find(tier => 
        tierAvailability[tier] && tierAvailability[tier].includes(colorCode)
      );

      if (availableTier) {
        console.log(`Switching from ${state.tier} to ${availableTier} for color ${colorCode}`);
        state.tier = availableTier;

        // Update the radio button
        const newRadio = document.querySelector(`input[name="tier"][value="${availableTier}"]`);
        if (newRadio) {
          newRadio.checked = true;
        }
      }
    }
  };

  // ============================================
  // Core Validation & Search
  // ============================================
  
  /**
   * Validate the current frame selection against color/tier
   * This function checks if the selected frame is available in the
   * current color and tier combination, then updates the UI accordingly
   */
  async function validateCurrent() {
    console.log('[SBC] validateCurrent() called');
    console.log('[SBC] Current state:', {
      handle: state.handle,
      colorCode: state.colorCode,
      tier: state.tier
    });

    const handle = state.handle ? toSlug(state.handle) : '';
    if (!handle) { 
      console.log('[SBC] No handle provided, resetting UI');
      resetUI(); 
      return; 
    }

    console.log('[SBC] === VALIDATION DEBUG ===');
    console.log('[SBC] BEFORE buildSkuCode - state.colorCode:', state.colorCode);
    console.log('[SBC] BEFORE buildSkuCode - state.tier:', state.tier);
    console.log('[SBC] BEFORE buildSkuCode - typeof state.tier:', typeof state.tier);
    
    const skuCode = buildSkuCode(state.colorCode, state.tier).toUpperCase();
    
    console.log('[SBC] AFTER buildSkuCode - Generated SKU:', skuCode);
    console.log('[SBC] Frame handle:', handle);
    console.log('[SBC] Full state object:', JSON.stringify(state, null, 2));

    setLoading(true);
    showMsg('');
    priceDisplays.forEach(el => el && (el.textContent = '$--'));
    atcButtons.forEach(btn => {
      if (!btn) return;
      btn.disabled = true;
      btn.textContent = 'Checking...';
    });

    try {
      const fetchUrl = `${window.Shopify?.routes?.root || '/'}products/${handle}.js`;
      console.log('[SBC] Fetching product from:', fetchUrl);
      
      const res = await fetch(fetchUrl, { credentials: 'same-origin' });
      console.log('[SBC] Fetch response status:', res.status);
      
      if (res.status === 404) {
        console.log('[SBC] Product not found (404)');
        resetUI();
        showMsg('Product not found. Check your frame name.');
        return;
      }
      if (!res.ok) throw new Error('Bad response');
      
      const product = await res.json();
      console.log('[SBC] Product fetched successfully:', product.title);
      console.log('[SBC] Product has', product.variants?.length, 'variants');

      selectedFrameLabels.forEach(label => label && (label.textContent = product.title || state.handle));

      console.log('[SBC] Searching for variant with SKU containing:', skuCode);
      const match = (product.variants || []).find(v => {
        const sku = (v.sku || '').toUpperCase();
        
        // For Standard tier, look for exact color code match (e.g., G15)
        // For AMP/Pro tier, look for tier prefix + color + P (e.g., AMG15P, ZSG15P)
        let matches = false;
        if (state.tier === 'Standard') {
          // Standard: look for exact color code (G15, not ZSG15P)
          matches = sku.includes(skuCode) && !sku.includes('AM') && !sku.includes('ZS');
        } else {
          // AMP/Pro: look for tier prefix + color + P
          matches = sku.includes(skuCode);
        }
        
        console.log('[SBC] Checking variant SKU:', sku, '- Match:', matches, `(tier: ${state.tier})`);
        if (matches) {
          console.log('[SBC] ✅ Found matching variant:', {
            sku: sku,
            price: v.price,
            available: v.available,
            tier: state.tier,
            colorCode: state.colorCode
          });
        }
        return matches;
      });

      if (!match) {
        console.log('[SBC] ❌ No matching variant found for SKU:', skuCode);
        console.log('[SBC] Available SKUs in product:', (product.variants || []).map(v => v.sku));
        resetUI();
        showMsg('This color/tier is not available for that frame.');
        return;
      }

      state.matchedVariant = match;
      console.log('[SBC] Matched variant stored in state');

      if (!match.available) {
        console.log('[SBC] Variant found but NOT available');
        priceDisplays.forEach(el => el && (el.textContent = money(match.price)));
        atcButtons.forEach(btn => {
          if (!btn) return;
          btn.disabled = true;
          btn.textContent = 'Unavailable in this color/tier';
        });
        showMsg('Unavailable in this color/tier.');
        setValid(false);
        return;
      }

      // Available
      console.log('[SBC] ✅ Variant is available! Setting up UI...');
      priceDisplays.forEach(el => el && (el.textContent = money(match.price)));
      atcButtons.forEach(btn => {
        if (!btn) return;
        btn.disabled = false;
        btn.textContent = 'Add to Cart';
      });
      showMsg('');
      setValid(true);
      console.log('[SBC] Price set to:', money(match.price));
      console.log('[SBC] ATC enabled, validation set to TRUE');

    } catch (err) {
      console.error('[SBC] ❌ Error during validation:', err);
      resetUI();
      showMsg('Something went wrong. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
      console.log('[SBC] Validation complete, loading state cleared');
    }
  }

  // ============================================
  // Event Handlers
  // ============================================
  
  // Color swatch click
  if ($swatchList) {
    $swatchList.addEventListener('click', (e) => {
      const a = e.target.closest('a');
      if (!a) return;
      e.preventDefault();
      
      // swatch id is the base code
      state.colorCode = a.id;

      // Update URL parameter
      updateUrlParameters();
      
      // visual active state
      $swatchList.querySelectorAll('a.active-thumb').forEach(x => x.classList.remove('active-thumb'));
      a.classList.add('active-thumb');
      
      // Update mobile toggle with cloned swatch (mobile only)
      updateMobileToggleSwatch();

      // Get lens color data from global variable
      const lensData = window.lensColorsData;
      console.log('Using lens colors data:', lensData);
      
      // Build the full tier-specific color code for price lookup
      const fullColorCode = buildSkuCode(state.colorCode, state.tier);
      console.log('Looking up tier-specific color code:', fullColorCode, 'for tier:', state.tier);
      
      // Find the color data by _key (base color for display data)
      let colorData;
      if (Array.isArray(lensData)) {
        colorData = lensData.find(c => c._key === state.colorCode);
      } else {
        console.error('Lens colors data is not an array:', lensData);
      }
      
      // Find tier-specific data for price
      let tierColorData;
      if (Array.isArray(lensData)) {
        tierColorData = lensData.find(c => c._key === fullColorCode);
      }

      console.log('Swatch clicked:', {
        colorCode: state.colorCode,
        fullColorCode: fullColorCode,
        colorName: colorData?.name,
        colorData: colorData,
        tierColorData: tierColorData
      });

      const specDetails = resolveSpecData(tierColorData, colorData);

      if (colorData) {
        console.log('Updating all cells with color data:', colorData);

        // Update color name
        const colorName = document.getElementById('sbc-color-name');
        if (colorName) {
          colorName.textContent = colorData.colorname || colorData['lens-view'] || colorData.name || 'Unknown';
        }

        // Update tier subtitle
        const tierSubtitle = document.getElementById('sbc-tier-subtitle');
        if (tierSubtitle) {
          tierSubtitle.innerHTML = getTierBadgeHTML(state.tier);
        }

      const lensDescription = document.getElementById('sbc-lens-description');
      if (lensDescription) {
        lensDescription.textContent = colorData?.['lens-color'] || specDetails?.['lens-color'] || '--';
      }

        // Update .sbc-specs background gradient with primary and secondary colors
        const specsContainer = document.querySelector('.sbc-specs');
        if (specsContainer && colorData.primarycolor && colorData.secondarycolor) {
          specsContainer.style.setProperty('--primarycolor', colorData.primarycolor);
          specsContainer.style.setProperty('--secondarycolor', colorData.secondarycolor);
          console.log('[SBC] Set CSS variables:', {
            primarycolor: colorData.primarycolor,
            secondarycolor: colorData.secondarycolor
          });
        }

        // Update specs
        const brightness = document.getElementById('sbc-spec-brightness');
        if (brightness) {
          const vlt = (specDetails && specDetails.vlt != null) ? `${specDetails.vlt}%` : '--';
          brightness.textContent = vlt;
        }

        const view = document.getElementById('sbc-spec-view');
        if (view) {
          view.textContent = specDetails?.['lens-view'] || '--';
        }

        // Update VLT icon active state based on light condition
        const lightCondition = specDetails?.light; // e.g., "low-light", "mixed-light", "bright-light"
        const vltIcons = document.querySelectorAll('.sbc-vlt-icon');
        vltIcons.forEach(icon => {
          icon.classList.remove('active');
          if (icon.getAttribute('data-light') === lightCondition) {
            icon.classList.add('active');
          }
        });

        const vltLabelValue = document.getElementById('sbc-vlt-label-value');
        if (vltLabelValue) {
          vltLabelValue.textContent = formatLightLabel(lightCondition);
        }

        // Update price based on current tier (use tier-specific data)
        const price = document.getElementById('sbc-price');
        const priceToUse = tierColorData?.price || colorData.price;
        if (price && priceToUse) {
          price.textContent = `$${priceToUse.toFixed(2)}`;
          console.log('Updated price to:', priceToUse, 'from tier data:', tierColorData?._key);
        }

        priceDisplays.forEach(el => {
          if (!el) return;
          el.textContent = priceToUse ? `$${priceToUse.toFixed(2)}` : '$--';
        });

        const priceBottom = document.getElementById('sbc-price-bottom');
        if (priceBottom && priceToUse) {
          priceBottom.textContent = `$${priceToUse.toFixed(2)}`;
        }

        // Update feature cells with checkmarks and X's
        // Use tier-specific data for features that vary by tier
        const featureData = tierColorData || colorData;
        const features = {
          'feat-poly': 'yes', // Always polycarbonate
          'feat-uv': 'yes', // Always UV protected
          'feat-mirrored': featureData['mirrored'],
          'feat-polarized': featureData['polarized'],
          'feat-color-enhancing': featureData['color_enhancing']
        };

        Object.entries(features).forEach(([id, value]) => {
          const element = document.getElementById(id);
          if (element) {
            const iconElement = element.querySelector('.sbc-feature-icon');
            if (iconElement) {
              // Toggle checkx class for X icon
              if (value === 'yes') {
                iconElement.classList.remove('checkx');
                iconElement.setAttribute('data-feature', 'yes');
              } else {
                iconElement.classList.add('checkx');
                iconElement.setAttribute('data-feature', 'no');
              }
            }
            element.style.opacity = '1';
            element.style.filter = 'none';
          }

          // Update bottom features
          const elementBottom = document.getElementById(id + '-bottom');
          if (elementBottom) {
            elementBottom.style.opacity = value ? '1' : '0.3';
            elementBottom.style.filter = value ? 'none' : 'grayscale(100%)';
          }
        });

        // Update activities chips (top section)
        const activitiesListEl = document.getElementById('sbc-activities');
        if (activitiesListEl) {
          const activitiesSource = (tierColorData && tierColorData.activities) ? tierColorData.activities : colorData.activities;
          let activities = [];
          if (activitiesSource) {
            activities = activitiesSource
              .split(',')
              .map(activity => activity.trim())
              .filter(activity => activity.length > 0)
              .slice(0, 4)
              .map(activity => activity.replace(/-/g, ' '));
          }

          console.log('[SBC] Activities update (sidebar sync) for', state.colorCode, 'tier', state.tier, activities);

          activitiesListEl.innerHTML = '';
          if (activities.length === 0) {
            const placeholder = document.createElement('li');
            placeholder.textContent = '--';
            activitiesListEl.appendChild(placeholder);
          } else {
            activities.forEach(activity => {
              const chip = document.createElement('li');
              chip.textContent = activity;
              activitiesListEl.appendChild(chip);
            });
          }
        }

        // Update images
        const images = {
          'sbc-primary-image': `https://cdn.shopify.com/s/files/1/1542/2169/files/226-${state.colorCode.toLowerCase()}.jpg`,
          'sbc-lens-hero': colorData['alt-1'] ? `https://cdn.shopify.com/s/files/1/1542/2169/files/${colorData['alt-1']}` : colorData.primary_image,
          'sbc-alt-1': colorData['alt-2'] ? `https://cdn.shopify.com/s/files/1/1542/2169/files/${colorData['alt-2']}` : colorData.image_full,
          // 'sbc-sim-0': colorData['sim-0'] ? `https://cdn.shopify.com/s/files/1/1542/2169/files/${colorData['sim-0']}` : colorData.image_metal,
          // 'sbc-sim-1': colorData['sim-1'] ? `https://cdn.shopify.com/s/files/1/1542/2169/files/${colorData['sim-1']}` : colorData.image_half,
          'sbc-alt-2': colorData['alt-act'] ? `https://cdn.shopify.com/s/files/1/1542/2169/files/${colorData['alt-act']}` : colorData.primary_image
        };

        // Update simulator images (sim-0 and sim-1) using tier-specific data
        // Use tier-specific data if available, otherwise fall back to base color data
        const simData = tierColorData || colorData;
        
        // Update sim-0 (no-lens image) - the image inside .cd-resize-img
        const sim0Img = document.querySelector('.cd-resize-img img');
        if (sim0Img && simData['sim-0']) {
          const sim0Url = `https://cdn.shopify.com/s/files/1/1542/2169/files/${simData['sim-0']}`;
          console.log('[SBC] Updating sim-0 (no-lens) image:', sim0Url, 'tier:', state.tier);
          sim0Img.src = sim0Url;
          sim0Img.setAttribute('data-src', sim0Url);
        }
        
        // Update sim-1 (color image) - simImg
        const simImg = document.getElementById('simImg');
        if (simImg && simData['sim-1']) {
          const sim1Url = `https://cdn.shopify.com/s/files/1/1542/2169/files/${simData['sim-1']}`;
          console.log('[SBC] Updating sim-1 (color) image:', sim1Url, 'tier:', state.tier);
          simImg.src = sim1Url;
          simImg.setAttribute('data-src', sim1Url);
        }

        // Update simulator label
        const colorName2 = document.getElementById('colorName2');
        if (colorName2) {
          const displayName = colorData.colorname || colorData['lens-view'] || colorData.name || 'Unknown';
          colorName2.textContent = displayName;
          console.log('Updating simulator label:', displayName);
        }

        Object.entries(images).forEach(([id, url]) => {
          const img = document.getElementById(id);
          if (img && url) {
            console.log('Updating image:', { id, url });
            img.src = url;
          }
        });

        // Update specs
        const specsForSidebar = resolveSpecData(tierColorData, colorData);
        const specs = {
          'sbc-spec-brightness': (specsForSidebar && specsForSidebar.vlt != null) ? `${specsForSidebar.vlt}%` : '--',
          'sbc-spec-view': specsForSidebar?.['lens-view'] || '--',
          'sbc-price': (specsForSidebar && typeof specsForSidebar.price === 'number') ? `$${specsForSidebar.price.toFixed(2)}` : '$--'
        };

        Object.entries(specs).forEach(([id, value]) => {
          const el = document.getElementById(id);
          if (el) {
            console.log('Updating spec:', { id, value });
            el.textContent = value;
          }
        });

      } else {
        console.warn('Color data not found for code:', state.colorCode);
      }

      updateTierAvailability(state.colorCode);
      
      validateCurrent();
    });
  }

  // Tier change
  $tierRadios.forEach(r => {
    r.addEventListener('change', () => {
      if (r.checked) {
        console.log('[SBC] Tier changed from', state.tier, 'to', r.value);
        state.tier = r.value;
        console.log('[SBC] state.tier is now:', state.tier);
        
        // Update price based on new tier
        const fullColorCode = buildSkuCode(state.colorCode, state.tier);
        console.log('[SBC] Looking up price for tier-specific code:', fullColorCode);
        const colorData = window.lensColorsData.find(c => c._key === fullColorCode);
        
        if (colorData) {
          // Update price
          if (colorData.price) {
            const price = document.getElementById('sbc-price');
            if (price) {
              price.textContent = `$${colorData.price.toFixed(2)}`;
              console.log('[SBC] Updated price to:', colorData.price);
            }
            
            const priceBottom = document.getElementById('sbc-price-bottom');
            if (priceBottom) {
              priceBottom.textContent = `$${colorData.price.toFixed(2)}`;
            }
          }
          
          // Update VLT and View specs based on tier-specific data
          const tierSpecDetails = resolveSpecData(null, colorData);

          const brightness = document.getElementById('sbc-spec-brightness');
          if (brightness) {
          const vltValue = (tierSpecDetails && tierSpecDetails.vlt != null) ? `${tierSpecDetails.vlt}%` : '--';
          console.log('[SBC] Tier change top VLT', state.colorCode, state.tier, vltValue);
            brightness.textContent = vltValue;
          }

          const brightnessBottom = document.getElementById('sbc-spec-brightness-bottom');
          if (brightnessBottom) {
            const vltValue = (tierSpecDetails && tierSpecDetails.vlt != null) ? `${tierSpecDetails.vlt}%` : '--';
          console.log('[SBC] Tier change bottom VLT', state.colorCode, state.tier, vltValue);
            brightnessBottom.textContent = vltValue;
          }

          const viewEl = document.getElementById('sbc-spec-view');
          if (viewEl) {
            viewEl.textContent = tierSpecDetails?.['lens-view'] || '--';
          }

          const viewBottomEl = document.getElementById('sbc-spec-view-bottom');
          if (viewBottomEl) {
            viewBottomEl.textContent = tierSpecDetails?.['lens-view'] || '--';
          }

          // Update VLT icon active state based on tier-specific light condition
          const lightCondition = tierSpecDetails?.light;
          const vltIcons = document.querySelectorAll('.sbc-vlt-icon');
          vltIcons.forEach(icon => {
            icon.classList.remove('active');
            if (icon.getAttribute('data-light') === lightCondition) {
              icon.classList.add('active');
            }
          });

          const vltLabelValue = document.getElementById('sbc-vlt-label-value');
          if (vltLabelValue) {
            vltLabelValue.textContent = formatLightLabel(lightCondition);
          }

          // Update features based on tier-specific data
          const features = {
            'feat-poly': 'yes', // Always polycarbonate
            'feat-uv': 'yes', // Always UV protected
            'feat-mirrored': colorData['mirrored'],
            'feat-polarized': colorData['polarized'],
            'feat-color-enhancing': colorData['color_enhancing']
          };
          
          Object.entries(features).forEach(([id, value]) => {
            const element = document.getElementById(id);
            if (element) {
              const iconElement = element.querySelector('.sbc-feature-icon');
              if (iconElement) {
                // Toggle checkx class for X icon
                if (value === 'yes') {
                  iconElement.classList.remove('checkx');
                  iconElement.setAttribute('data-feature', 'yes');
                } else {
                  iconElement.classList.add('checkx');
                  iconElement.setAttribute('data-feature', 'no');
                }
              }
            }
          });
          
          console.log('[SBC] Updated features for tier:', state.tier, 'polarized:', colorData['polarized']);
          
          // Update activities chips for new tier
          const activitiesListEl = document.getElementById('sbc-activities');
          if (activitiesListEl) {
            let activities = [];
            if (colorData.activities) {
              activities = colorData.activities
                .split(',')
                .map(activity => activity.trim())
                .filter(activity => activity.length > 0)
                .slice(0, 4)
                .map(activity => activity.replace(/-/g, ' '));
            }

            console.log('[SBC] Tier change activities update for', state.colorCode, 'tier', state.tier, activities);

            activitiesListEl.innerHTML = '';
            if (activities.length === 0) {
              const placeholder = document.createElement('li');
              placeholder.textContent = '--';
              activitiesListEl.appendChild(placeholder);
            } else {
              activities.forEach(activity => {
                const chip = document.createElement('li');
                chip.textContent = activity;
                activitiesListEl.appendChild(chip);
              });
            }
          }

          // Update .sbc-specs background gradient with primary and secondary colors
          const specsContainer = document.querySelector('.sbc-specs');
          if (specsContainer && colorData.primarycolor && colorData.secondarycolor) {
            specsContainer.style.setProperty('--primarycolor', colorData.primarycolor);
            specsContainer.style.setProperty('--secondarycolor', colorData.secondarycolor);
            console.log('[SBC] Tier change - Set CSS variables:', {
              primarycolor: colorData.primarycolor,
              secondarycolor: colorData.secondarycolor
            });
          }
          
          // Update simulator images (sim-0 and sim-1) based on tier-specific data
          // Update sim-0 (no-lens image) - the image inside .cd-resize-img
          const sim0Img = document.querySelector('.cd-resize-img img');
          if (sim0Img && colorData['sim-0']) {
            const sim0Url = `https://cdn.shopify.com/s/files/1/1542/2169/files/${colorData['sim-0']}`;
            console.log('[SBC] Tier change - Updating sim-0 (no-lens) image:', sim0Url, 'tier:', state.tier);
            sim0Img.src = sim0Url;
            sim0Img.setAttribute('data-src', sim0Url);
          }
          
          // Update sim-1 (color image) - simImg
          const simImg = document.getElementById('simImg');
          if (simImg && colorData['sim-1']) {
            const sim1Url = `https://cdn.shopify.com/s/files/1/1542/2169/files/${colorData['sim-1']}`;
            console.log('[SBC] Tier change - Updating sim-1 (color) image:', sim1Url, 'tier:', state.tier);
            simImg.src = sim1Url;
            simImg.setAttribute('data-src', sim1Url);
          }
        }
        
        // Update tier subtitle
        const tierSubtitle = document.getElementById('sbc-tier-subtitle');
        if (tierSubtitle) {
          tierSubtitle.innerHTML = getTierBadgeHTML(state.tier);
        }

    const lensDescription = document.getElementById('sbc-lens-description');
    if (lensDescription) {
      const baseColor = window.lensColorsData.find(c => c._key === state.colorCode);
      lensDescription.textContent = baseColor?.['lens-color'] || colorData?.['lens-color'] || '--';
    }
        
        updateUrlParameters();
        validateCurrent();
      }
    });
  });

  // Search typing / enter
  let debounceTimer;
  const DEBOUNCE_MS = 250;

  const onSearchChange = () => {
    if (!state.handle) {
      resetUI();
      clearResults();
      return;
    }

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      if (state.handle.length >= 3) {
        filterFunctionFrames(state.handle);
      }
    }, DEBOUNCE_MS);
  };

  const syncInputValues = (source) => {
    frameInputs.forEach(input => {
      if (input && input !== source) {
        input.value = source ? source.value : state.handle || '';
      }
    });
  };

  const attachInputEvents = (input) => {
    if (!input) return;

    input.addEventListener('keyup', (e) => {
      const originalValue = input.value || '';
      const corrected = normalizeBrandTypos(originalValue);
      let finalValue = corrected;

      // If the user just typed space after a normalized brand, preserve the space and append it
      if (corrected && originalValue.length > corrected.length && originalValue.slice(-1) === ' ') {
        finalValue = `${corrected} `;
      }

      const valueChanged = finalValue !== originalValue;
      if (valueChanged) {
        input.value = finalValue;
        try {
          const nextPos = finalValue.length;
          input.setSelectionRange(nextPos, nextPos);
        } catch (err) {
          // ignore if selection can't be set
        }
      }

      state.handle = finalValue.trim();
      syncInputValues(input);

      if (e.key === 'Enter') {
        validateCurrent();
        clearResults();
        setSearchbarActive(false);
      } else {
        onSearchChange();
        setSearchbarActive(true);
      }
    });

    input.addEventListener('focus', () => {
      const corrected = normalizeBrandTypos(input.value || '');
      if (corrected !== input.value) {
        input.value = corrected;
        state.handle = corrected.trim();
        syncInputValues(input);
      }
      setSearchbarActive(true);
      if (state.handle.length >= 3) {
        filterFunctionFrames(state.handle);
      }
    });

    input.addEventListener('blur', () => {
      if (!state.handle) {
        setSearchbarActive(false);
      }
    });
  };

  frameInputs.forEach(attachInputEvents);

  // Frame search result click handler
  document.addEventListener('click', (e) => {
    if (e.target.classList.contains('frameSearchEntry')) {
      const selectedTitle = e.target.dataset.title || e.target.value;
      frameInputs.forEach(input => input && (input.value = selectedTitle));
      state.handle = selectedTitle;
      clearResults();
      setSearchbarActive(false);
      validateCurrent();
    }
  });

  const handleAtcClick = async (btn) => {
    if (!btn) return;
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (btn.disabled || !state.matchedVariant?.id) {
        return;
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: state.matchedVariant.id, quantity: 1 })
        });
        if (!res.ok) throw new Error('ATC failed');

        if (cfg.autoOpenCart && typeof window.openCart === 'function') {
          window.openCart();
        } else {
          showMsg('Added to cart.');
        }
      } catch (err) {
        showMsg('Could not add to cart. Try again.');
        console.error(err);
      }
    });
  };

  atcButtons.forEach(handleAtcClick);

  // ============================================
  // Integration with specific-frames-search
  // ============================================
  
  // Listen for the custom event dispatched by the frame search
  window.addEventListener('frameModelSelected', function(e) {
    if (e.detail && e.detail.handle) {
      state.handle = e.detail.handle;
      const displayValue = e.detail.title || e.detail.handle;
      frameInputs.forEach(input => input && (input.value = displayValue));
      setSearchbarActive(true);
      clearResults();
      validateCurrent();
    }
  });

  // Also check for jQuery-based events if theme uses them
  if (window.jQuery) {
    jQuery(document).on('frameSelected', function(e, data) {
      if (data && data.handle) {
        state.handle = data.handle;
        const displayValue = data.title || data.handle;
        frameInputs.forEach(input => input && (input.value = displayValue));
        setSearchbarActive(true);
        clearResults();
        validateCurrent();
      }
    });
  }

  // ============================================
  // Frame Search Implementation
  // ============================================
  
  function filterFunctionFrames(input) {
    console.log('[SBC] filterFunctionFrames called with input:', input);
    if (typeof window.searchBarJson === 'undefined' || !window.searchBarJson) {
      console.warn('[SBC] window.searchBarJson is not available! Search will not work.');
      return;
    }

    if (input.length < 3) {
      buildResultsListFrames([]);
      return;
    }

    const normalize = (value = '') =>
      value
        .toUpperCase()
        .replace(/&/g, 'AND')
        .replace(/[^0-9A-Z]/g, '');

    const rawTokens = input.trim().split(/\s+/).filter(Boolean);
    const synNormalize = value => {
      const normalized = normalize(value);
      if (
        normalized === 'RAYBAN' ||
        normalized === 'RAY' ||
        normalized === 'RAYBANAVIATOR' ||
        normalized === 'RAYBANWAYFARER' ||
        normalized === 'RAYBANJUSTIN'
      ) {
        return 'RAYBAN';
      }
      if (normalized === 'OAKELY') {
        return 'OAKLEY';
      }
      if (normalized === 'WARBY' || normalized === 'WARBYPARKER') {
        return 'WARBYPARKER';
      }
      return normalized;
    };
    const normalizedTokens = rawTokens.map(synNormalize);
    const normalizedInput = synNormalize(input);
    const brandFilterToken = normalizedTokens.length > 1 ? normalizedTokens[0] : null;
    const modelTokens = brandFilterToken ? normalizedTokens.slice(1) : normalizedTokens;

    const getBrandTokenVariants = (brandNorm, token) => {
      const variants = new Set([token]);
      if (brandNorm === 'RAYBAN' && token.includes('AVI')) {
        variants.add('AVI');
        variants.add('AVIATOR');
        variants.add('RB302');
        variants.add('RB303');
        variants.add('RB304');
      }
      return Array.from(variants);
    };

    const matches = [];
    const pushMatch = (brand, frame) => {
      if (!frame || matches.some(entry => entry.handle === frame.handle)) return;
      matches.push({
        brand: brand.brand,
        frame: frame.name,
        handle: frame.handle
      });
    };

    window.searchBarJson.forEach(brand => {
      const brandNorm = synNormalize(brand.brand);
      if (!brandNorm) return;
      const brandMatchesFilter = brandFilterToken ? brandNorm.includes(brandFilterToken) : true;
      if (!brandMatchesFilter) return;

      if (brandFilterToken) {
        if (modelTokens.length > 0) {
          brand.frames.forEach(frame => {
            if (matches.length >= 9) return;
            const frameNorm = normalize(frame.name);
            const handleNorm = normalize(frame.handle || '');
            const matchesAllTokens = modelTokens.every(token => {
              const variants = getBrandTokenVariants(brandNorm, token);
              return variants.some(variant => frameNorm.includes(variant) || handleNorm.includes(variant));
            });
            if (matchesAllTokens) {
              pushMatch(brand, frame);
            }
          });
        }
        // If we still have room, top off with additional frames from the filtered brand
        if (matches.length < 9) {
          brand.frames.forEach(frame => {
            if (matches.length >= 9) return;
            pushMatch(brand, frame);
          });
        }
      } else {
        // General search across all brands
        if (normalizedTokens.length > 1) {
          const [firstToken, ...restTokens] = normalizedTokens;
          const remainingToken = restTokens.join('');
          if (firstToken && brandNorm.includes(firstToken)) {
            brand.frames.forEach(frame => {
              if (matches.length >= 9) return;
              const frameNorm = normalize(frame.name);
              if (frameNorm.includes(remainingToken)) {
                pushMatch(brand, frame);
              }
            });
          }
        }

        if (matches.length < 9 && (brandNorm.includes(normalizedInput) || normalizedInput.includes(brandNorm))) {
          brand.frames.slice(0, Math.max(0, 9 - matches.length)).forEach(frame => pushMatch(brand, frame));
        }

        brand.frames.forEach(frame => {
          if (matches.length >= 9) return;
          const frameNorm = normalize(frame.name);
          if (frameNorm.includes(normalizedInput) && !matches.some(m => m.handle === frame.handle)) {
            pushMatch(brand, frame);
          }
        });
      }
    });

    console.log('[SBC] Found', matches.length, 'matches for:', input);
    buildResultsListFrames(matches.slice(0, 9));
  }

  function buildResultsListFrames(matches) {
    resultsLists.forEach(list => {
      if (!list) return;
      list.innerHTML = '';

      if (matches.length === 0) {
        const noResults = document.createElement('div');
        noResults.classList.add('SBA__dropdown--no-results');
        noResults.innerHTML = '<i>0 Results Found</i><br>Try a different search term';
        list.appendChild(noResults);
        return;
      }

      matches.forEach(match => {
        const li = document.createElement('li');
        const span = document.createElement('span');
        span.classList.add('frameSearchEntry');
        span.innerHTML = `<b>${match.brand}</b> ${match.frame}`;
        span.dataset.title = `${match.brand} ${match.frame}`;
        span.dataset.handle = match.handle;
        li.appendChild(span);
        list.appendChild(li);
      });
    });
  }

  // ============================================
  // Initialization
  // ============================================
  
  // Mark default color (BL) active if present
  const defaultSwatch = document.querySelector('#product-swatches-list a#BL');
  if (defaultSwatch) defaultSwatch.classList.add('active-thumb');
  
  // Update mobile toggle with cloned swatch on initial load (mobile only)
  updateMobileToggleSwatch();
  
  // Reflect initial "Select Frame" CTA
  resetUI();

  // ============================================
  // Public API - Expose for Frame Search Panel
  // ============================================
  
  /**
   * Expose state and validateCurrent globally for the frame search panel
   * to trigger validation after frame selection
   */
  window.state = state;
  window.validateCurrent = validateCurrent;
  
  console.log('[SBC] Public API exposed:', {
    state: window.state,
    validateCurrent: typeof window.validateCurrent
  });

  // ============================================
  // Mobile Swatch Clone Function
  // ============================================
  
  /**
   * Clones the active swatch to the mobile toggle button (mobile only)
   * This allows users to see the selected color even when the dropdown is closed
   */
  function updateMobileToggleSwatch() {
    const mobileToggle = document.getElementById('mobile-toggle');
    if (!mobileToggle) return;
    
    // Find the active swatch
    const activeSwatch = document.querySelector('#product-swatches-list a.active-thumb .swatch');
    if (!activeSwatch) {
      // No active swatch found, remove any existing cloned swatch
      const existingClone = mobileToggle.querySelector('.mobile-toggle-swatch');
      if (existingClone) {
        existingClone.remove();
      }
      return;
    }
    
    // Remove any existing cloned swatch
    const existingClone = mobileToggle.querySelector('.mobile-toggle-swatch');
    if (existingClone) {
      existingClone.remove();
    }
    
    // Clone the swatch element
    const swatchClone = activeSwatch.cloneNode(true);
    swatchClone.classList.add('mobile-toggle-swatch');
    
    // Find the text span in the toggle button
    const toggleText = mobileToggle.querySelector('span:not(.mobile-toggle-swatch)');
    if (toggleText) {
      // Insert the swatch after the text
      toggleText.insertAdjacentElement('afterend', swatchClone);
    } else {
      // If no text span, just append to button
      mobileToggle.appendChild(swatchClone);
    }
    
    console.log('[MOBILE] Cloned swatch to mobile toggle:', swatchClone.classList.toString());
  }

  // ============================================
  // Mobile Modal Handler
  // ============================================
  
  const mobileToggle = document.getElementById('mobile-toggle');
  const mobilePanel = document.getElementById('mobile-panel');
  const mobileClose = document.getElementById('mobile-close');
  const mobileApply = document.getElementById('mobile-apply');
  const mobileCancel = document.getElementById('mobile-cancel');
  const mobilePanelContent = document.getElementById('mobile-panel-content');
  const sidebarScroll = document.querySelector('.sidebar-scroll');
  
  console.log('[MOBILE] Initializing mobile modal handler');
  console.log('[MOBILE] Elements found:', {
    mobileToggle: !!mobileToggle,
    mobilePanel: !!mobilePanel,
    mobileClose: !!mobileClose,
    mobileApply: !!mobileApply,
    mobileCancel: !!mobileCancel,
    mobilePanelContent: !!mobilePanelContent,
    sidebarScroll: !!sidebarScroll
  });
  
  if (mobileToggle && mobilePanel && mobilePanelContent && sidebarScroll) {
    // Clone sidebar content to mobile panel on first open
    let contentCloned = false;
    let mobileSwatchList = null;
    let mobileTierRadios = [];
    let mobilePendingColor = null;
    let mobilePendingTier = null;

    const syncMobilePanelSelections = () => {
      if (!mobilePanelContent) return;

      if (mobileSwatchList) {
        mobileSwatchList.querySelectorAll('a').forEach(link => link.classList.remove('active-thumb'));
        if (mobilePendingColor) {
          const selectorId = (window.CSS && CSS.escape) ? CSS.escape(mobilePendingColor) : mobilePendingColor;
          const activeLink = mobileSwatchList.querySelector(`a#${selectorId}`);
          if (activeLink) {
            activeLink.classList.add('active-thumb');
          }
        }
      }

      if (mobilePendingColor) {
        if (!tierAvailability[mobilePendingTier] || !tierAvailability[mobilePendingTier]?.includes(mobilePendingColor)) {
          const availableTier = Object.keys(tierAvailability).find(tier => tierAvailability[tier]?.includes(mobilePendingColor));
          if (availableTier) {
            mobilePendingTier = availableTier;
          }
        }
      }

      mobileTierRadios.forEach(radio => {
        const isAvailable = mobilePendingColor ? tierAvailability[radio.value]?.includes(mobilePendingColor) : true;
        radio.disabled = !isAvailable;
        const tierButton = radio.closest('label') || radio.parentElement;
        if (tierButton) {
          tierButton.classList.toggle('tier-disabled', !isAvailable);
        }
        radio.checked = radio.value === mobilePendingTier;
      });
    };

    const closeMobilePanel = () => {
      mobilePanel.classList.remove('open');
      mobileToggle.classList.remove('open');
    };

    const resetPendingSelections = () => {
      mobilePendingColor = state.colorCode;
      mobilePendingTier = state.tier;
      syncMobilePanelSelections();
    };

    const applyPendingSelections = () => {
      const colorToApply = mobilePendingColor || state.colorCode;
      const tierToApply = mobilePendingTier || state.tier;

      state.tier = tierToApply;
      const desktopTierRadio = document.querySelector(`input[name="tier"][value="${tierToApply}"]`);
      if (desktopTierRadio) {
        desktopTierRadio.checked = true;
      }

      runSwatchClickLogic(colorToApply);
      closeMobilePanel();
    };
    
    const openMobilePanel = () => {
      console.log('[MOBILE] Opening mobile panel, contentCloned:', contentCloned);

      if (!contentCloned) {
        console.log('[MOBILE] Cloning sidebar content...');
        const clonedContent = sidebarScroll.cloneNode(true);
        mobilePanelContent.innerHTML = '';
        mobilePanelContent.appendChild(clonedContent);
        
        // Remove frame search / ATC sections from clone
        mobilePanelContent.querySelectorAll('.sbc-search-section, .sidebar-atc').forEach(el => el.remove());

        mobileSwatchList = mobilePanelContent.querySelector('#product-swatches-list');
        mobileTierRadios = Array.from(mobilePanelContent.querySelectorAll('input[name="tier"]'));

        if (mobileSwatchList) {
          mobileSwatchList.addEventListener('click', (e) => {
            const a = e.target.closest('a');
            if (!a) return;
            e.preventDefault();
            mobilePendingColor = a.id;
            syncMobilePanelSelections();
          });
        }

        mobileTierRadios.forEach(radio => {
          radio.addEventListener('change', () => {
            if (!radio.checked) return;
            mobilePendingTier = radio.value;
            syncMobilePanelSelections();
          });
        });

        contentCloned = true;
        console.log('[MOBILE] Content cloned successfully');
      }

      mobilePendingColor = state.colorCode;
      mobilePendingTier = state.tier;
      syncMobilePanelSelections();

      mobilePanel.classList.add('open');
      mobileToggle.classList.add('open');
      console.log('[MOBILE] Panel opened');
    };
    
    mobileToggle.addEventListener('click', () => {
      console.log('[MOBILE] Mobile toggle button clicked');
      openMobilePanel();
    });
    
    const handleCancel = () => {
      console.log('[MOBILE] Mobile cancel triggered');
      resetPendingSelections();
      closeMobilePanel();
    };

    const handleApply = () => {
      console.log('[MOBILE] Mobile apply triggered with pending color:', mobilePendingColor, 'tier:', mobilePendingTier);
      applyPendingSelections();
    };

    mobileClose && mobileClose.addEventListener('click', handleCancel);
    mobileCancel && mobileCancel.addEventListener('click', handleCancel);
    mobileApply && mobileApply.addEventListener('click', handleApply);
    
    console.log('[MOBILE] Event listeners attached successfully');
  } else {
    console.warn('[MOBILE] Some mobile elements not found, mobile modal disabled');
  }

  });
});